// Public API for the annotation system
import React, { createRef } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { AnnotationEditor } from './AnnotationEditor';
import { AnnotationViewer } from './AnnotationViewer';
import { computeContentBoxPosition } from '../player/utils/computeContentBoxPosition';
/**
 * Resolve an AnnotationTarget (string id or HTMLElement) to an HTMLElement,
 * logging an error and returning null if a string id has no matching node.
 */
function resolveAnnotationTarget(target, context) {
    if (typeof target === 'string') {
        const el = document.getElementById(target);
        if (!el) {
            console.error(`${context}: target element not found:`, target);
            return null;
        }
        return el;
    }
    return target;
}
// --- Mount functions for non-React usage ---
window.mountAnnotationEditor = function mountAnnotationEditor(targetOrId, options = {}) {
    const target = resolveAnnotationTarget(targetOrId, 'AnnotationEditor');
    if (!target) {
        return { unmount: () => { }, get api() { return null; } };
    }
    // Guard: remove any existing editor before mounting a new one
    const existingRoot = target.querySelector('[data-annotation-root="editor"]');
    if (existingRoot)
        existingRoot.remove();
    // Also remove any orphaned toolbar portals from the previous editor
    target.querySelectorAll(':scope > .hekate-annotation-toolbar').forEach((el) => el.remove());
    // Lock the wrapper's current height so adding the toolbar flex child
    // shrinks the video element instead of growing the player.
    target.style.height = target.offsetHeight + 'px';
    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-annotation-root', 'editor');
    target.appendChild(wrapper);
    target.classList.add('hekate-annotation-active');
    // Position and size the annotation root to match the video's
    // object-fit:contain content area. The toolbar is portalled as a
    // separate flex child in the wrapper — it doesn't affect the root.
    const videoEl = target.querySelector('video');
    let annotationResizeObs;
    const syncEditorRoot = () => {
        if (!videoEl || !videoEl.videoWidth)
            return;
        const targetRect = target.getBoundingClientRect();
        const videoRect = videoEl.getBoundingClientRect();
        const pos = computeContentBoxPosition({
            videoNativeWidth: videoEl.videoWidth,
            videoNativeHeight: videoEl.videoHeight,
            videoElementWidth: videoEl.clientWidth,
            videoElementHeight: videoEl.clientHeight,
            videoTopInWrapper: videoRect.top - targetRect.top,
            videoLeftInWrapper: videoRect.left - targetRect.left,
        });
        if (!pos)
            return;
        wrapper.style.position = 'absolute';
        wrapper.style.width = pos.width + 'px';
        wrapper.style.height = pos.height + 'px';
        wrapper.style.top = pos.top + 'px';
        wrapper.style.left = pos.left + 'px';
        wrapper.style.right = 'auto';
        wrapper.style.bottom = 'auto';
        wrapper.style.margin = '0';
    };
    if (videoEl && typeof ResizeObserver !== 'undefined') {
        annotationResizeObs = new ResizeObserver(syncEditorRoot);
        annotationResizeObs.observe(videoEl);
        syncEditorRoot();
    }
    // targetRef points to the annotation root (wrapper) whose CSS aspect-ratio
    // matches the video, so useContainerSize measures the video content area.
    const targetRef = { current: wrapper };
    const apiRef = createRef();
    const root = createRoot(wrapper);
    const { initialShapes, isEditing, onSave, onDiscard, isFullscreen, defaultColor, onMounted } = options;
    flushSync(() => {
        root.render(React.createElement(AnnotationEditor, {
            ref: apiRef,
            targetRef,
            initialShapes,
            isEditing,
            onSave,
            onDiscard,
            isFullscreen,
            defaultColor,
            onMounted,
        }));
    });
    return {
        unmount: () => {
            annotationResizeObs?.disconnect();
            // Clone the toolbar BEFORE React unmount destroys the portal, so we
            // can animate the clone while React is safely torn down. Unmounting
            // React immediately prevents re-renders (ResizeObserver, state changes)
            // from recreating the toolbar during the slide-out animation.
            const toolbar = target.querySelector(':scope > .hekate-annotation-toolbar');
            let toolbarClone = null;
            if (toolbar) {
                toolbarClone = toolbar.cloneNode(true);
                toolbarClone.style.animation = 'hekate-toolbar-slide-out 0.3s ease forwards';
                toolbar.parentElement.insertBefore(toolbarClone, toolbar);
            }
            // Unmount React immediately — prevents any re-render interference
            root.unmount();
            wrapper.remove();
            target.classList.remove('hekate-annotation-active');
            if (toolbarClone) {
                let cleaned = false;
                const cleanup = () => {
                    if (cleaned)
                        return;
                    cleaned = true;
                    toolbarClone.remove();
                    target.style.height = '';
                };
                toolbarClone.addEventListener('animationend', cleanup, { once: true });
                // Safety: clean up after 400ms if animationend doesn't fire
                setTimeout(cleanup, 400);
            }
            else {
                target.style.height = '';
            }
        },
        get api() {
            return apiRef.current;
        },
    };
};
window.mountAnnotationViewer = function mountAnnotationViewer(targetOrId, shapes, options) {
    const target = resolveAnnotationTarget(targetOrId, 'AnnotationViewer');
    if (!target) {
        return {
            unmount: () => { },
            get api() { return null; },
            updateShapes: () => { },
        };
    }
    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-annotation-root', 'viewer');
    target.appendChild(wrapper);
    target.classList.add('hekate-annotation-active');
    // targetRef points to the annotation root (wrapper) whose CSS aspect-ratio
    // matches the video, so useContainerSize measures the video content area.
    const targetRef = { current: wrapper };
    const apiRef = createRef();
    const root = createRoot(wrapper);
    const onMounted = options?.onMounted;
    function renderViewer(currentShapes, mounted) {
        flushSync(() => {
            root.render(React.createElement(AnnotationViewer, {
                ref: apiRef,
                targetRef,
                shapes: currentShapes,
                onMounted: mounted,
            }));
        });
    }
    renderViewer(shapes, onMounted);
    // Auto-dismiss viewer when user starts playback or manually seeks.
    // We listen for 'play' (user hits play) and 'hekate:userseek' (user
    // clicks the timeline slider). We intentionally do NOT listen for the
    // native 'seeked' event because the programmatic seek to show the
    // annotation also fires 'seeked', which would immediately unmount it.
    // The custom 'hekate:userseek' event is only dispatched by the SeekBar
    // (user-initiated seeks), not by programmatic seeks.
    const mediaEl = target.querySelector('video, audio');
    const handleDismiss = () => handle.unmount();
    mediaEl?.addEventListener('play', handleDismiss, { once: true });
    mediaEl?.addEventListener('hekate:userseek', handleDismiss, { once: true });
    const handle = {
        unmount: () => {
            mediaEl?.removeEventListener('play', handleDismiss);
            mediaEl?.removeEventListener('hekate:userseek', handleDismiss);
            root.unmount();
            wrapper.remove();
            target.classList.remove('hekate-annotation-active');
        },
        get api() {
            return apiRef.current;
        },
        updateShapes: (newShapes) => {
            renderViewer(newShapes);
        },
    };
    return handle;
};
// --- Re-exports for React usage ---
export { AnnotationEditor } from './AnnotationEditor';
export { AnnotationViewer } from './AnnotationViewer';
export { exportSvg } from './utils/svgExport';
